import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Eye,
  Pencil,
  LayoutTemplate,
  Settings2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Trash2,
  Copy,
  Plus,
  X,
  FileBarChart,
} from "lucide-react";
import { useAuthStore } from "../../state/auth";
import { useTranslation } from "../../i18n";
import { HospitalSelector } from "../../components/dashboard/HospitalSelector";
import { useStickyHospital } from "../../hooks/useStickyHospital";
import { ReportsPage } from "./ReportsPage";
import { PageHeader, Card } from "../../components/reports/ui";
import { AiAssistBar, Walkthrough } from "../../components/reports/AiAssistBar";
import {
  ReportDataProvider,
  useReportData,
  listSavedReports,
  saveReport,
  deleteReport,
  type CacheEntry,
  type SavedReportSummary,
  type AiAssistResult,
  type AiStep,
} from "../../components/reports/reportApi";
import { DataSourcePanel } from "../../components/reports/DataSourcePanel";
import { WidgetToolbar } from "../../components/reports/WidgetToolbar";
import { WidgetConfigPanel } from "../../components/reports/WidgetConfig";
import { WidgetView } from "../../components/reports/WidgetView";
import { ReportPreview } from "../../components/reports/ReportPreview";
import { ExportMenu } from "../../components/reports/ExportMenu";
import {
  TEMPLATES,
  makeWidget,
  widgetFromCatalog,
  widgetFromAi,
  type CatalogItem,
  type ReportConfig,
  type WidgetConfig,
  type WidgetType,
} from "../../components/reports/types";

const SIZE_SPAN: Record<WidgetConfig["size"], string> = {
  small: "md:col-span-2",
  medium: "md:col-span-3",
  large: "md:col-span-6",
};
const HEADERLESS = new Set<WidgetType>(["text", "metric"]);

function blankReport(author: string): ReportConfig {
  return TEMPLATES[0].build(author);
}

export const ReportBuilderPage: React.FC = () => {
  const { user } = useAuthStore();
  const author = user?.name || "";
  const isAdmin = user?.role === "ADMIN" || user?.role === "Administrator";
  const isSupervisor = user?.role === "SUPERVISOR";
  // ADMIN and SUPERVISOR default to "all my hospitals" (undefined): the report
  // data endpoint scopes SUPERVISOR to their assigned hospitals server-side, so
  // this aggregates exactly their hospitals. They can narrow via the selector.
  const canSelectHospital = isAdmin || isSupervisor;
  const [report, setReport] = useState<ReportConfig>(() => blankReport(author));
  const [hospitalId, setHospitalId] = useStickyHospital(
    canSelectHospital ? undefined : user?.hospital_id || undefined
  );

  // Backfill the author once auth resolves.
  useEffect(() => {
    if (author) setReport((r) => (r.author ? r : { ...r, author }));
  }, [author]);

  return (
    <ReportDataProvider range={report.dateRange} hospitalId={hospitalId}>
      <Workspace
        report={report}
        setReport={setReport}
        author={author}
        canSelectHospital={canSelectHospital}
        hospitalId={hospitalId}
        setHospitalId={setHospitalId}
      />
    </ReportDataProvider>
  );
};

export default ReportBuilderPage;

function Workspace({
  report,
  setReport,
  author,
  canSelectHospital,
  hospitalId,
  setHospitalId,
}: {
  report: ReportConfig;
  setReport: React.Dispatch<React.SetStateAction<ReportConfig>>;
  author: string;
  canSelectHospital: boolean;
  hospitalId: string | undefined;
  setHospitalId: (id: string | undefined) => void;
}) {
  const { t, lang } = useTranslation();
  const { snapshot } = useReportData();
  const qc = useQueryClient();
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(report.widgets.length === 0);
  const [showSettings, setShowSettings] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [walkSteps, setWalkSteps] = useState<AiStep[] | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const dragFrom = useRef<number | null>(null);

  const selected = report.widgets.find((w) => w.id === selectedId) || null;
  const hl = (id: string) => (highlight === id ? " ring-4 ring-amber-300 ring-offset-2" : "");

  // ----- AI assistant -----
  function applyAi(result: AiAssistResult) {
    const widgets = result.report.widgets.map((w) => widgetFromAi(w));
    setReport((r) => ({
      ...r,
      title: result.report.title || r.title,
      subtitle: result.report.subtitle || r.subtitle,
      // The period is what actually filters every widget. The assistant used to
      // put "September 2026" only in the title, so the report showed all-time data.
      // null from the server = the request named no period (all time).
      ...(result.report.dateRange !== undefined ? { dateRange: result.report.dateRange ?? {} } : {}),
      widgets,
    }));
    setSelectedId(null);
    setShowTemplates(false);
    setWalkSteps(null);
    setHighlight(null);
  }

  // ----- widget mutations -----
  function patchReport(patch: Partial<ReportConfig>) {
    setReport((r) => ({ ...r, ...patch }));
  }
  function updateWidget(id: string, patch: Partial<WidgetConfig>) {
    setReport((r) => ({ ...r, widgets: r.widgets.map((w) => (w.id === id ? { ...w, ...patch } : w)) }));
  }
  function addWidget(type: WidgetType) {
    const w = makeWidget(type);
    setReport((r) => ({ ...r, widgets: [...r.widgets, w] }));
    setSelectedId(w.id);
  }
  function addFromCatalog(item: CatalogItem) {
    const w = widgetFromCatalog(item);
    setReport((r) => ({ ...r, widgets: [...r.widgets, w] }));
    setSelectedId(w.id);
  }
  function removeWidget(id: string) {
    setReport((r) => ({ ...r, widgets: r.widgets.filter((w) => w.id !== id) }));
    setSelectedId((s) => (s === id ? null : s));
  }
  function duplicateWidget(id: string) {
    setReport((r) => {
      const idx = r.widgets.findIndex((w) => w.id === id);
      if (idx < 0) return r;
      const { id: _omit, ...rest } = r.widgets[idx];
      const clone = makeWidget(rest.type, { ...rest, title: `${rest.title} ${t("reportBuilder.copySuffix", "(copy)")}` });
      const widgets = [...r.widgets];
      widgets.splice(idx + 1, 0, clone);
      return { ...r, widgets };
    });
  }
  function moveWidget(from: number, to: number) {
    setReport((r) => {
      if (to < 0 || to >= r.widgets.length) return r;
      const widgets = [...r.widgets];
      const [m] = widgets.splice(from, 1);
      widgets.splice(to, 0, m);
      return { ...r, widgets };
    });
  }

  // ----- save / load -----
  async function save(asTemplate = false) {
    if (saving) return;
    setSaving(true);
    try {
      const res = await saveReport({
        id: savedId,
        title: report.title,
        is_template: asTemplate,
        config: report,
      });
      setSavedId(res.report.id);
      setSavedAt(new Intl.DateTimeFormat(lang, { hour: "numeric", minute: "2-digit" }).format(new Date()));
      qc.invalidateQueries({ queryKey: ["saved-reports"] });
    } catch (e) {
      alert(`${t("reportBuilder.couldntSave", "Couldn’t save:")} ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  function loadTemplate(build: (a: string) => ReportConfig) {
    setReport(build(author));
    setSavedId(null);
    setSelectedId(null);
    setShowTemplates(false);
  }
  function loadSaved(sr: SavedReportSummary) {
    const cfg = sr.config && Array.isArray(sr.config.widgets) ? (sr.config as ReportConfig) : blankReport(author);
    setReport({ ...cfg, author: cfg.author || author });
    setSavedId(sr.is_template ? null : sr.id);
    setSelectedId(null);
    setShowTemplates(false);
  }

  const getEntries = useMemo(() => () => snapshot() as CacheEntry[], [snapshot]);

  const offscreen: React.CSSProperties =
    mode === "edit" ? { position: "absolute", left: -10000, top: 0, width: 1024 } : {};

  return (
    <div>
      <PageHeader
        title={t("reportBuilder.title", "Report Builder")}
        subtitle={t("reports.builderSubtitle", "Design professional reports — no coding required.")}
        actions={
          <div className="no-print flex flex-wrap items-center gap-2">
            {canSelectHospital && <HospitalSelector selectedHospitalId={hospitalId} onHospitalChange={setHospitalId} />}
            <button
              onClick={() => setShowTemplates(true)}
              className={`inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50${hl("templates")}`}
            >
              <LayoutTemplate size={16} /> {t("reportBuilder.templates", "Templates")}
            </button>
            <div className={`flex overflow-hidden rounded-lg border border-gray-200${hl("preview")}`}>
              <button
                onClick={() => setMode("edit")}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold ${mode === "edit" ? "bg-blue-700 text-white" : "bg-white text-gray-700"}`}
              >
                <Pencil size={15} /> {t("common.edit", "Edit")}
              </button>
              <button
                onClick={() => setMode("preview")}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold ${mode === "preview" ? "bg-blue-700 text-white" : "bg-white text-gray-700"}`}
              >
                <Eye size={15} /> {t("reportBuilder.preview", "Preview")}
              </button>
            </div>
            <span className={`inline-block rounded-lg${hl("export")}`}>
              <ExportMenu title={report.title} getNode={() => previewRef.current} getEntries={getEntries} onPreview={() => setMode("preview")} />
            </span>
          </div>
        }
      />

      {savedAt && <p className="no-print -mt-2 mb-3 text-xs text-emerald-600">{t("reportBuilder.savedAt", "Saved at {time}").replace("{time}", savedAt)}</p>}

      {/* AI assistant: plain-language / sketch → report layout */}
      {mode === "edit" && (
        <div className={`rounded-xl${hl("ai_bar")}`}>
          <AiAssistBar onApply={applyAi} onWalkthrough={(steps) => setWalkSteps(steps)} />
        </div>
      )}

      {/* Report settings bar */}
      <Card className="no-print mb-4 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">{t("reportBuilder.reportTitle", "Report title")}</label>
            <input
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-base font-semibold focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={report.title}
              onChange={(e) => patchReport({ title: e.target.value })}
            />
          </div>
          <div className="lg:col-span-2">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">{t("reportBuilder.subtitle", "Subtitle")}</label>
            <input
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={report.subtitle}
              onChange={(e) => patchReport({ subtitle: e.target.value })}
              placeholder={t("reportBuilder.optionalSubtitle", "Optional subtitle")}
            />
          </div>
        </div>
        <button
          onClick={() => setShowSettings((v) => !v)}
          className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800"
        >
          <Settings2 size={14} /> {showSettings ? t("reportBuilder.hideSettings", "Hide settings") : t("reportBuilder.moreSettings", "More settings")}
          <ChevronDown size={13} className={showSettings ? "rotate-180" : ""} />
          {/* Keep the active period visible even while the settings are collapsed —
              an empty range means every widget reads ALL history. */}
          <span className={`ml-2 rounded px-1.5 py-0.5 font-medium ${report.dateRange.start || report.dateRange.end ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
            {t("reportBuilder.period", "Period: {range}").replace(
              "{range}",
              report.dateRange.start || report.dateRange.end
                ? `${report.dateRange.start || "…"} – ${report.dateRange.end || "…"}`
                : t("reportBuilder.allTime", "All time")
            )}
          </span>
        </button>
        {showSettings && (
          <div className="mt-3 grid grid-cols-1 gap-3 border-t border-gray-100 pt-3 md:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">{t("reportBuilder.dateFromLabel", "Date from")}</label>
              <input
                type="date"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={report.dateRange.start || ""}
                onChange={(e) => patchReport({ dateRange: { ...report.dateRange, start: e.target.value || undefined } })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">{t("reportBuilder.dateToLabel", "Date to")}</label>
              <input
                type="date"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={report.dateRange.end || ""}
                onChange={(e) => patchReport({ dateRange: { ...report.dateRange, end: e.target.value || undefined } })}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">{t("reportBuilder.author", "Author")}</label>
              <input
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={report.author}
                onChange={(e) => patchReport({ author: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
                <input type="checkbox" checked={report.includeLogo} onChange={(e) => patchReport({ includeLogo: e.target.checked })} />
                {t("reportBuilder.includeLogo", "Include GuardAI logo")}
              </label>
            </div>
          </div>
        )}
      </Card>

      {mode === "edit" && (
        <>
          <Card className={`no-print mb-4 p-4${hl("toolbar")}`}>
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-500">{t("reportBuilder.addWidget", "Add a widget")}</h2>
            <WidgetToolbar onAdd={addWidget} />
          </Card>

          <div className="no-print grid grid-cols-1 gap-4 lg:grid-cols-[15rem_1fr_18rem]">
            {/* Left: data fields */}
            <Card className={`h-fit p-4${hl("data_fields")}`}>
              <DataSourcePanel onAdd={addFromCatalog} />
            </Card>

            {/* Center: canvas */}
            <div className={`rounded-xl${hl("canvas")}`}>
              {report.widgets.length === 0 ? (
                <Card className="flex flex-col items-center justify-center gap-3 py-20 text-center">
                  <FileBarChart size={40} className="text-gray-200" />
                  <p className="font-medium text-gray-700">{t("reportBuilder.canvasEmpty", "Your canvas is empty")}</p>
                  <p className="max-w-sm text-sm text-gray-400">{t("reportBuilder.canvasEmptyHint", "Add a widget from the toolbar above, click a data field on the left, or start from a template.")}</p>
                  <button
                    onClick={() => setShowTemplates(true)}
                    className="mt-1 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    <LayoutTemplate size={16} /> {t("reportBuilder.startFromTemplate", "Start from a template")}
                  </button>
                </Card>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
                  {report.widgets.map((w, idx) => {
                    const isSel = w.id === selectedId;
                    return (
                      <div
                        key={w.id}
                        draggable
                        onDragStart={() => (dragFrom.current = idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (dragFrom.current != null && dragFrom.current !== idx) moveWidget(dragFrom.current, idx);
                          dragFrom.current = null;
                        }}
                        onClick={() => setSelectedId(w.id)}
                        className={`${SIZE_SPAN[w.size]} group cursor-pointer rounded-xl border bg-white p-4 shadow-sm transition ${
                          isSel ? "border-blue-400 ring-2 ring-blue-200" : "border-gray-100 hover:border-gray-300"
                        }`}
                      >
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <GripVertical size={14} className="shrink-0 cursor-grab text-gray-300" />
                            {HEADERLESS.has(w.type) ? (
                              <span className="truncate text-xs font-semibold uppercase tracking-wide text-gray-400">{w.title}</span>
                            ) : (
                              <span className="truncate text-sm font-bold text-gray-900">{w.title}</span>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition group-hover:opacity-100">
                            <button onClick={(e) => { e.stopPropagation(); moveWidget(idx, idx - 1); }} className="rounded p-1 text-gray-400 hover:bg-gray-50" title={t("reportBuilder.moveUp", "Move up")}>
                              <ChevronUp size={14} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); moveWidget(idx, idx + 1); }} className="rounded p-1 text-gray-400 hover:bg-gray-50" title={t("reportBuilder.moveDown", "Move down")}>
                              <ChevronDown size={14} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); duplicateWidget(w.id); }} className="rounded p-1 text-gray-400 hover:bg-gray-50" title={t("reportBuilder.duplicate", "Duplicate")}>
                              <Copy size={13} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); removeWidget(w.id); }} className="rounded p-1 text-red-400 hover:bg-red-50" title={t("reportBuilder.remove", "Remove")}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                        <WidgetView config={w} editing onChange={(patch) => updateWidget(w.id, patch)} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right: config */}
            <Card className={`h-fit p-4${hl("config_panel")}`}>
              {selected ? (
                <WidgetConfigPanel
                  config={selected}
                  onChange={(patch) => updateWidget(selected.id, patch)}
                  onDelete={() => removeWidget(selected.id)}
                  onDuplicate={() => duplicateWidget(selected.id)}
                />
              ) : (
                <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-gray-400">
                  <Settings2 size={26} className="text-gray-200" />
                  {t("reportBuilder.selectWidget", "Select a widget to configure it.")}
                </div>
              )}
            </Card>
          </div>
        </>
      )}

      {/* Branded preview — visible in preview mode; off-screen but live in edit
          mode so export/print always have a rendered, branded copy. */}
      <div className="report-print-root" style={offscreen}>
        <ReportPreview ref={previewRef} report={report} />
      </div>

      {mode === "preview" && (
        <p className="no-print mt-3 text-center text-xs text-gray-400">
          {t("reportBuilder.previewHint", "This is how your report will export and print. Use the Export button above to download.")}
        </p>
      )}

      {/* Generated reports & analytics — the former standalone Reports page, merged
          in below the builder so everything reporting-related lives in one place. */}
      <div className="no-print mt-10 border-t border-gray-200 pt-8">
        <h2 className="mb-4 text-lg font-bold text-gray-900">{t("reportBuilder.reportsAnalytics", "Reports & Analytics")}</h2>
        <ReportsPage />
      </div>

      {showTemplates && (
        <TemplateModal
          onClose={() => setShowTemplates(false)}
          onPick={loadTemplate}
          onLoadSaved={loadSaved}
          onDeleteSaved={async (id) => {
            await deleteReport(id);
            qc.invalidateQueries({ queryKey: ["saved-reports"] });
          }}
          onSaveTemplate={() => save(true)}
        />
      )}

      {walkSteps && walkSteps.length > 0 && (
        <Walkthrough
          steps={walkSteps}
          onHighlight={setHighlight}
          onClose={() => {
            setWalkSteps(null);
            setHighlight(null);
          }}
        />
      )}
    </div>
  );
}

function TemplateModal({
  onClose,
  onPick,
  onLoadSaved,
  onDeleteSaved,
  onSaveTemplate,
}: {
  onClose: () => void;
  onPick: (build: (a: string) => ReportConfig) => void;
  onLoadSaved: (sr: SavedReportSummary) => void;
  onDeleteSaved: (id: string) => Promise<void>;
  onSaveTemplate: () => void;
}) {
  const { t } = useTranslation();
  const { data: saved } = useQuery({
    queryKey: ["saved-reports"],
    queryFn: () => listSavedReports(),
  });

  return (
    <div className="no-print fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-black/40 p-4 sm:p-8" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">{t("reportBuilder.startNewReport", "Start a new report")}</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-50">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {TEMPLATES.map((tpl) => (
            <button
              key={tpl.id}
              onClick={() => onPick(tpl.build)}
              className="group rounded-xl border border-gray-100 p-4 text-left transition hover:border-blue-300 hover:bg-blue-50"
            >
              <div className="flex items-center gap-2">
                <LayoutTemplate size={16} className="text-blue-500" />
                <span className="font-semibold text-gray-900">{tpl.name}</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">{tpl.description}</p>
            </button>
          ))}
        </div>

        {saved && saved.reports.length > 0 && (
          <div className="mt-6">
            <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">{t("reportBuilder.savedReports", "Saved reports")}</h3>
            <div className="space-y-1.5">
              {saved.reports.map((sr) => (
                <div key={sr.id} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2">
                  <button onClick={() => onLoadSaved(sr)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                    <FileBarChart size={15} className="shrink-0 text-gray-400" />
                    <span className="truncate text-sm font-medium text-gray-800">{sr.title}</span>
                    <span className="shrink-0 text-xs text-gray-400">{sr.author_name}</span>
                  </button>
                  <button onClick={() => onDeleteSaved(sr.id)} className="ml-2 rounded p-1 text-red-400 hover:bg-red-50" title={t("common.delete", "Delete")}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
          <button onClick={onSaveTemplate} className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800">
            <Plus size={14} /> {t("reportBuilder.saveAsTemplate", "Save current report as a shared template")}
          </button>
          <button onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            {t("common.cancel", "Cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
